// =============================================================================
// Canonical role model for the web console — SINGLE SOURCE OF TRUTH.
// =============================================================================
// Roles come from the `combird_roles` JWT claim (set by the Zitadel actions-server,
// which mirrors the user's ACTIVE project authorizations). The claim is PER-PROJECT:
//   combird_roles: {
//     sms:            { roles: [org_admin, org_sender, org_analytics] },  ← channel
//     whatsapp/email/rcs: { roles: [...] },                               ← channel
//     internal-tools: { roles: [platform_admin, vault_admin] },           ← PLATFORM tier
//   }
//
// TWO SCOPES — do not conflate them:
//   • CHANNEL scope  → this app is the SMS product (product_code 'sms'); org-level
//     feature gating uses `combird_roles.sms.roles`. Flattening ALL projects into one set
//     is wrong: it would leak a different channel's org role (e.g. org_admin in email)
//     into the SMS portal.
//   • PLATFORM scope → the Admin Panel + cross-tenant tooling are gated on platform-tier
//     roles (platform_admin/admin/admin_agent/support_agent), which live on the
//     `internal-tools` project — NOT on sms. Scoping the admin check to sms would hide the
//     Admin Panel even for a real platform admin.
//
// The legacy single `role` (localStorage/redux, read by MenuDrawer + AuthRoute) is derived
// from the CHANNEL roles ∪ the PLATFORM roles — see effectiveRoles() / derivePrimaryRole().
// `admin` is a legacy alias for platform_admin, kept for backward-compat with older tokens.

// This app's channel/project. Override via REACT_APP_PROJECT if the bundle is reused.
export const CURRENT_PROJECT = process.env.REACT_APP_PROJECT || 'sms';

// Platform-tier role keys — granted on internal-tools, NOT a channel. Project-independent.
const PLATFORM_ROLE_KEYS = [
  'platform_admin', 'admin', 'admin_agent', 'support_agent',
  // Current Zitadel platform-tier vocabulary (internal-tools project) — the
  // legacy *_agent keys above are kept for backward-compat with old tokens.
  'platform_support', 'platform_billing', 'platform_viewer',
];

// Highest-privilege first. Used to collapse the effective role set to one primary role.
export const ROLE_PRIORITY = [
  'platform_admin',
  'admin',            // legacy alias for a platform admin
  // Platform functional tier OUTRANKS org roles: a support agent who also
  // holds org roles (their own org's sms project, for test sends) must still
  // land in the admin-panel experience, not the customer one.
  'platform_support', // daily-ops support agent (onboarding, accounts, emails)
  'platform_billing', // wallet top-ups / billing admin
  'org_admin',
  'admin_agent',
  'support_agent',
  'platform_viewer',
];

// Roles that unlock the platform ADMIN PANEL: its nav item, its route guard
// (AuthRoute isAdmin), and the post-login redirect to /admin-panel.
export const ADMIN_PANEL_ROLES = [
  'platform_admin', 'admin', 'admin_agent', 'support_agent',
  'platform_support', 'platform_billing',
];

// Platform admin TOOLS that exclude read-only support (Manage Operators, Rate Limits).
export const ADMIN_TOOL_ROLES = ['platform_admin', 'admin', 'admin_agent'];

// "True" platform admins (full rights), excluding support agents. Drives isPlatformAdmin.
export const PLATFORM_ADMIN_ROLES = ['platform_admin', 'admin'];

// Roles for ONE project from the combird_roles claim (channel-scoped gating).
export const rolesForProject = (claims, project = CURRENT_PROJECT) =>
  claims?.combird_roles?.[project]?.roles || [];

// All role keys across every project — used ONLY to detect platform-tier roles
// (which live on internal-tools). Never use this for channel feature gating.
const allRoleKeys = (claims) => {
  const cr = claims?.combird_roles;
  if (!cr || typeof cr !== 'object') return [];
  const set = new Set();
  Object.values(cr).forEach((p) => (p?.roles || []).forEach((r) => set.add(r)));
  return [...set];
};

// Effective roles for THIS app = current channel's org roles ∪ any platform-tier roles
// the user holds. This is what derivePrimaryRole runs on, so the Admin Panel shows when
// platform_admin is present (from internal-tools) while channel decisions stay sms-scoped.
export const effectiveRoles = (claims) => {
  const channel = rolesForProject(claims);
  const platform = allRoleKeys(claims).filter((r) => PLATFORM_ROLE_KEYS.includes(r));
  return [...new Set([...channel, ...platform])];
};

// Collapse a role list to one primary role (highest privilege wins).
export const derivePrimaryRole = (roles = []) =>
  ROLE_PRIORITY.find((r) => roles.includes(r)) || 'user';

// Support-tier roles: full admin-panel workflow but customer-org features
// (groups/contacts) and platform tools stay hidden.
export const SUPPORT_TIER_ROLES = ['platform_support', 'platform_billing', 'support_agent'];

export const isAdminPanelRole = (role) => ADMIN_PANEL_ROLES.includes(role);
export const isSupportTierRole = (role) => SUPPORT_TIER_ROLES.includes(role);
export const isAdminToolRole = (role) => ADMIN_TOOL_ROLES.includes(role);
export const isPlatformAdminRole = (role) => PLATFORM_ADMIN_ROLES.includes(role);
